import { Formik, FormikProps } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useRef, useMemo, useState } from 'react';

import { ButtonColors } from 'types';
import { useAppSelector } from 'store';
import { Messages, AppRoutes } from 'config';
import { selectAuthUser } from 'store/slices/auth';
import { useUpdateMutation } from 'store/api/cart';
import { useMyCompanyInfoQuery } from 'store/api/company';
import { useAlert, isMobile, handleMutation } from 'hooks';
import { useCreateShippingMutation } from 'store/api/event';
import {
  formatPrice,
  calculateProductsPriceForApprovalDialog,
} from 'utils/helpers';
import {
  Drawer,
  Button,
  FormFooter,
  ButtonTypes,
  CommonStyles,
  ApprovalRequestDialog,
} from 'components';
import {
  useListQuery,
  useEditMutation,
  useCreateMutation,
  CustomerEditRequest,
  CustomerCreateRequest,
} from 'store/api/customers';

import * as Styles from './styles';
import { Package } from './package';
import { Products } from './products';
import { ShipmentSummary } from './summary';
import { FormValues, ShipmentProps } from './types';
import { CustomerShipmentInfo } from './customer-shipment-info';
import {
  stepTitles,
  initialValues,
  mobileStepTitles,
  getValidationSchemas,
} from './config';

const { CheckOut } = AppRoutes.Products;

export const CheckoutShipment = ({
  event,
  opened,
  cartData,
  checkoutId,
  navigateUrl,
  setShipmentOpened,
  withMaxProductQuantity,
}: ShipmentProps) => {
  const mobile = isMobile();
  const navigate = useNavigate();
  const { showErrorAlert, showSuccessAlert } = useAlert();
  const formRef = useRef<FormikProps<FormValues>>(null);
  const authUser = useAppSelector(selectAuthUser);
  const [currentStep, setCurrentStep] = useState(0);
  const [isInitialize, setInitialize] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const { data: company } = useMyCompanyInfoQuery();
  const { data: customers } = useListQuery({ limit: 10000 });

  const [edit, editMutation] = useEditMutation();
  const [update, updateMutation] = useUpdateMutation();
  const [create, createdMutation] = useCreateMutation();
  const [createFreeShipping, createFreeShippingMutation] =
    useCreateShippingMutation();
  handleMutation(editMutation);
  handleMutation(updateMutation);
  handleMutation(createdMutation);
  handleMutation(createFreeShippingMutation);

  const isLastStep = currentStep === 2;
  const isFirstStep = currentStep === 0;
  const onClose = () => {
    setShipmentOpened(false);
    setInitialize(false);
    setCurrentStep(0);
    setShowApprovalDialog(false);
    setShowSummary(false);
    formRef.current?.resetForm();
  };

  const goToNextStep = () => {
    setCurrentStep((step) => step + 1);
    formRef.current?.setTouched({
      zip: false,
      city: false,
      email: false,
      phone: false,
      state: false,
      address: false,
      lastName: false,
      firstName: false,
      recipientId: false,
      isNewCustomer: false,
    });
  };

  const {
    isShowApprovalDialog,
    totalMinPriceProducts,
    totalCartPriceProducts,
  } = calculateProductsPriceForApprovalDialog({
    company,
    cartData,
    showApprovalDialog,
  });

  const saveCustomer = async (values: FormValues) => {
    const commonValues = {
      zip: values.zip,
      city: values.city,
      state: values.state,
      phone: values.phone,
      email: values.email,
      address: values.address,
    };

    if (values.isNewCustomer) {
      const payload = {
        ...commonValues,
        isFromShipping: true,
        employeeId: authUser?.id,
        lastName: values.lastName,
        firstName: values.firstName,
      } as CustomerCreateRequest;

      const { id } = await create(payload).unwrap();

      return id;
    }

    const existing = customers?.rows?.find(
      (customer) => customer.id === Number(values.recipientId)
    );

    if (existing?.person) {
      const payload = {
        ...existing,
        ...existing.person,
        ...commonValues,
        id: String(values.recipientId),
      } as CustomerEditRequest;

      await edit(payload).unwrap();
    }

    return Number(values.recipientId);
  };

  const shipProducts = async ({
    values,
    eventId,
    customerId,
  }: {
    eventId: number;
    values: FormValues;
    customerId: number;
  }) => {
    // Only mark the line as shipped: price and quantity stay as the cart has them.
    const tasks = values.products.map((item) =>
      update({
        id: eventId,
        isShipping: true,
        cartItemId: item.id,
        shipping: {
          customerId,
          price: +values.shippingPrice,
          isFreeShipping: values.isFreeShipping,
        },
      }).unwrap()
    );
    // A line marked on an earlier try that the rep has now unticked goes back
    // to the sale (out-of-stock lines can only ship, so they stay marked).
    const ticked = new Set(values.products.map((item) => item.id));
    cartData?.items
      ?.filter(
        (item) =>
          item.isShipping && !item.isProductOutStorage && !ticked.has(item.id)
      )
      .forEach((item) =>
        tasks.push(
          update({
            id: eventId,
            isShipping: false,
            cartItemId: item.id,
          }).unwrap()
        )
      );

    const results = await Promise.allSettled(tasks);
    if (results.some((result) => result.status === 'rejected')) {
      showErrorAlert(
        'Some shipment items could not be saved. Please review the cart and retry.'
      );
      throw new Error('Shipment items could not be saved');
    }
  };

  const availableProducts = useMemo(
    () => (withMaxProductQuantity ? cartData?.items : undefined),
    [cartData?.items, withMaxProductQuantity]
  );

  const [productSchema, addressSchema, packageSchema] = useMemo(
    () => getValidationSchemas(availableProducts),
    [availableProducts]
  );
  const validationSchema = isLastStep
    ? productSchema.concat(addressSchema).concat(packageSchema)
    : [productSchema, addressSchema, packageSchema][currentStep];

  return (
    <Drawer opened={opened} topOffset="20%">
      <h2>Create shipment</h2>

      <Styles.StyledStepper
        active={currentStep}
        current={currentStep}
        onChange={setCurrentStep}
        steps={mobile ? mobileStepTitles : stepTitles}
      />

      <Formik<FormValues>
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (formValues, { setSubmitting }) => {
          if (!isLastStep) {
            setSubmitting(false);
            if (isFirstStep) {
              setShowSummary(true);
              return;
            }
            goToNextStep();
            return;
          }

          if (isShowApprovalDialog) {
            return setShowApprovalDialog(true);
          }

          try {
            const customerId = await saveCustomer(formValues);

            if (!navigateUrl) {
              await shipProducts({
                customerId,
                values: formValues,
                eventId: Number(event?.id),
              });

              showSuccessAlert(`Shipping ${Messages.CREATED_SUCCESSFULLY}`);
              // Continue to checkout after the shipment is saved (as on Dev).
              navigate(CheckOut);
              onClose();
              return;
            }

            if (checkoutId && formValues.isFreeShipping) {
              const response = await createFreeShipping({
                ...formValues,
                customerId,
                id: checkoutId,
                paymentMethods: [],
                shippingPrice: +formValues.shippingPrice,
              }).unwrap();

              if (response.id) {
                onClose();
              }
              return;
            }

            navigate(navigateUrl, {
              state: {
                checkoutId,
                formValues: { ...formValues, recipientId: String(customerId) },
              },
            });
            onClose();
          } catch (error) {
            console.error(error);
          }
        }}
      >
        {({ dirty, values, handleSubmit, isSubmitting }) => (
          <CommonStyles.Form $hasMinHeight>
            <Styles.FormContainer>
              {isFirstStep && (
                <Products
                  opened={opened}
                  cartData={cartData}
                  isInitialize={isInitialize}
                  setInitialize={setInitialize}
                  editableQuantity={withMaxProductQuantity}
                />
              )}
              {currentStep === 1 && (
                <CustomerShipmentInfo customers={customers?.rows} />
              )}
              {isLastStep && <Package />}
            </Styles.FormContainer>

            <FormFooter
              right={
                <Styles.ButtonWrapper>
                  <Button
                    fullWidth={mobile}
                    colorType={ButtonColors.LightBlue}
                    text={isFirstStep ? 'Cancel' : 'Back'}
                    onClick={
                      isFirstStep
                        ? onClose
                        : () => setCurrentStep((prevStep) => prevStep - 1)
                    }
                  />
                  <Button
                    fullWidth={mobile}
                    type={ButtonTypes.Submit}
                    disabled={!dirty || isSubmitting}
                    text={
                      isLastStep
                        ? `Checkout ${formatPrice(
                            values.products.reduce(
                              (acc, item) => acc + item.price * +item.quantity,
                              0
                            )
                          )}`
                        : 'Next'
                    }
                  />
                </Styles.ButtonWrapper>
              }
            />

            <ShipmentSummary
              open={showSummary}
              afterSale={!!checkoutId}
              products={values.products}
              cartItems={cartData?.items}
              onBack={() => setShowSummary(false)}
              onConfirm={() => {
                setShowSummary(false);
                goToNextStep();
              }}
            />

            <ApprovalRequestDialog
              event={event}
              cart={cartData}
              open={showApprovalDialog}
              onSuccess={() => handleSubmit()}
              onClose={() => setShowApprovalDialog(false)}
              totalMinPriceProducts={totalMinPriceProducts}
              isFromShipping={!!navigateUrl && !!checkoutId}
              totalCartPriceProducts={totalCartPriceProducts}
            />
          </CommonStyles.Form>
        )}
      </Formik>
    </Drawer>
  );
};
